import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export default function MultiSelectFilter({ options, selected, onChange, placeholder = 'All Members', minWidth = 180 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedSet = new Set(selected);

  const toggle = (value) => {
    if (selectedSet.has(value)) onChange(selected.filter(v => v !== value));
    else onChange([...selected, value]);
  };

  const clearAll = () => onChange([]);
  const selectAll = () => onChange(options.map(o => o.value));

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label || selected[0])
      : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px', borderRadius: 6,
          border: '1px solid var(--border)',
          background: 'var(--bg-input, var(--bg-card))',
          color: selected.length > 0 ? 'var(--text-primary)' : 'var(--text-muted)',
          fontSize: 13, minWidth, cursor: 'pointer',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <ChevronDown size={13} style={{ flexShrink: 0, opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          minWidth: Math.max(minWidth, 180), maxHeight: 280, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)' }}>
            <button onClick={selectAll} style={{ flex: 1, padding: '7px 12px', fontSize: 11, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Select All</button>
            <button onClick={clearAll} style={{ flex: 1, padding: '7px 12px', fontSize: 11, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', borderLeft: '1px solid var(--border)' }}>Clear</button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {options.map(opt => {
              const checked = selectedSet.has(opt.value);
              return (
                <label key={opt.value} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                  color: 'var(--text-primary)',
                  background: checked ? 'rgba(var(--brand-rgb, 99,102,241), 0.08)' : 'none',
                  transition: 'background 0.1s',
                }}>
                  <span style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: checked ? '2px solid var(--brand)' : '2px solid var(--border)',
                    background: checked ? 'var(--brand)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.1s',
                  }}>
                    {checked && <Check size={10} color="#fff" strokeWidth={3} />}
                  </span>
                  <input type="checkbox" checked={checked} onChange={() => toggle(opt.value)} style={{ display: 'none' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}